/**
 * settings-page.tsx — Khung màn Cài đặt: menu phụ dọc bên trái (cùng bộ với
 * Khách hàng, Kết nối, Đội ngũ…) + nội dung bên phải.
 *
 * CHỈ chứa cấu hình cấp tổ chức/hệ thống. Những gì của riêng người đăng nhập
 * (hồ sơ, mật khẩu, giao diện, phím tắt) nằm ở Cài đặt cá nhân (/account), vào
 * từ menu tài khoản — y như SaleSmartly tách /next/user/information khỏi
 * /next/setting. Quản lý nhân viên đã chuyển hẳn sang Đội ngũ. Mỗi mục khai báo `roles` / `perm` để chỉ
 * hiện với vai trò / quyền backend thực sự cho phép — theo nguyên tắc BRD §5.3.1
 * "không có quyền → không render", không render rồi disable.
 *
 * Module đang chọn nằm trên URL (`?m=`) để F5 hay gửi link vẫn đúng chỗ.
 */
import { Navigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { SubnavPanel, subnavItemClass } from '@/components/shared/section-subnav'
import { useAuthStore } from '@/stores/auth-store'
import { useMyPermissions } from '@/hooks/use-settings'
import { cn } from '@/lib/utils'
import { settingsGroupsForRole } from './settings-modules'

import { CompanyTab } from './company-tab'
import { TagsTab } from './tags-tab'
import { CustomerPipelineTab } from './customer-pipeline-tab'
import { MediaTab } from './media-tab'
import { ApiKeysTab } from './api-keys-tab'
import { AiConnectorTab } from './ai-connector-tab'
import { ActivityLogTab, LoginLogTab, PresenceLogTab } from './audit-log-tabs'
import { ConversationSettingsTab } from './conversation-settings-tab'
import { MessageNotifyTab } from './message-notify-tab'
import { DsarTab } from './dsar-tab'
import { PropertiesTab } from '@/pages/cdp/properties-tab'
import { EventsTab } from '@/pages/cdp/events-tab'

/** Phần vẽ của từng mục — dữ liệu (nhãn, quyền, mô tả) ở settings-modules.ts. */
const RENDER: Record<string, () => React.ReactNode> = {
  'company': () => <CompanyTab />,
  'tags': () => <TagsTab />,
  'customer-stages': () => <CustomerPipelineTab />,
  'customer-properties': () => <PropertiesTab />,
  'customer-events': () => <EventsTab />,
  'media': () => <MediaTab />,
  'api-keys': () => <ApiKeysTab />,
  'ai-connector': () => <AiConnectorTab />,
  'conversation-settings': () => <ConversationSettingsTab />,
  'message-notify': () => <MessageNotifyTab />,
  'audit-activity': () => <ActivityLogTab />,
  'audit-logins': () => <LoginLogTab />,
  'audit-presence': () => <PresenceLogTab />,
  'dsar': () => <DsarTab />,
}

export function SettingsPage() {
  const role = useAuthStore((s) => s.user?.role)
  const { data: perms } = useMyPermissions()
  const [params, setParams] = useSearchParams()
  // `settingsGroupsForRole()` trả getter đọc i18n.t() — gọi lại mỗi render để không đóng băng khi đổi ngôn ngữ
  // (xem tài liệu bàn giao mục 5 "Hằng chuỗi bị đóng băng lúc nạp module" — ở đây tránh bằng cách chỉ cần
  // component tự re-render đúng lúc, nên bắt buộc có useTranslation theo dõi namespace settings).
  const { t } = useTranslation('settings')

  const groups = settingsGroupsForRole(role, perms)
    .map((g) => ({ ...g, items: g.items.map((i) => ({ ...i, render: RENDER[i.id] })) }))

  const all = groups.flatMap((g) => g.items)
  // Link cũ "Cài đặt → Quản lý nhân viên" → Đội ngũ (nơi duy nhất quản lý người).
  if (params.get('m') === 'employees') return <Navigate to="/team/members" replace />
  // Mục cá nhân cũ (Hồ sơ của tôi, Mật khẩu & Bảo mật) → Cài đặt cá nhân.
  if (params.get('m') === 'profile' || params.get('m') === 'security') {
    return <Navigate to="/account/profile" replace />
  }
  // "Vòng đời khách hàng" cũ đã gộp vào "Pipeline khách hàng".
  if (params.get('m') === 'customer-lifecycle') return <Navigate to="/settings?m=customer-stages" replace />
  // Vai trò không được vào module trên URL → rơi về module đầu tiên hợp lệ.
  const active = all.find((m) => m.id === params.get('m')) ?? all[0]

  if (!active) return null
  const open = (id: string) => setParams({ m: id }, { replace: true })

  return (
    <div className="flex h-full min-h-0">
      <SubnavPanel title={t('settings:page.title')}>
        {groups.map((g) => (
          <div key={g.title} className="pb-2">
            <p className="mb-1 px-2 text-xs text-muted-foreground">{g.title}</p>
            <nav className="space-y-0.5">
              {g.items.map((m) => {
                const Icon = m.icon
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => open(m.id)}
                    title={m.description}
                    className={cn(subnavItemClass(m.id === active.id), 'w-full')}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    <span className="truncate">{m.label}</span>
                  </button>
                )
              })}
            </nav>
          </div>
        ))}
      </SubnavPanel>

      <div className="min-w-0 flex-1 overflow-y-auto overflow-x-hidden">
        <div className="mx-auto max-w-[1400px] space-y-4 px-6 py-5">
          {/* Màn hẹp không có menu phụ → hàng chip cuộn ngang thay thế. */}
          <div className="flex gap-1 overflow-x-auto md:hidden">
            {all.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => open(m.id)}
                className={cn(subnavItemClass(m.id === active.id), 'shrink-0 whitespace-nowrap')}
              >
                {m.label}
              </button>
            ))}
          </div>
          <div>
            <h1 className="text-lg font-medium">{active.label}</h1>
            <p className="mt-1 text-sm text-muted-foreground">{active.description}</p>
          </div>
          <section className="min-w-0">{active.render()}</section>
        </div>
      </div>
    </div>
  )
}
